import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Typography,
  Card,
  Tag,
  Badge,
  Input,
} from 'antd';
import {
  EditOutlined,
  FileTextOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { PromptTemplate } from '../../types';
import { promptTemplatesApi } from '../../services/api/promptTemplates';
import { useApi } from '../../hooks';
import { formatDate } from '../../utils';
import EditPromptTemplateModal from './EditPromptTemplateModal';

const { Title } = Typography;
const { Search } = Input;

const CUSTOM_PAGE_SIZE = 100;

const PromptTemplates: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentPage, setCurrentPage] = useState<number>(() => {
    const page = parseInt(searchParams.get('page') || '1', 10);
    return Number.isNaN(page) || page <= 0 ? 1 : page;
  });

  const [searchText, setSearchText] = useState<string>('');
  const [editingTemplate, setEditingTemplate] = useState<PromptTemplate | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const params = new URLSearchParams();
    if (currentPage > 1) params.set('page', String(currentPage));

    setSearchParams(params, { replace: true });
  }, [currentPage, setSearchParams]);

  const getPromptTemplates = useCallback(() => promptTemplatesApi.getPromptTemplates({
    pageIndex: 0, // Luôn lấy trang đầu tiên vì ta muốn lấy nhiều nhất có thể để filter client-side
    pageSize: CUSTOM_PAGE_SIZE,
  }), []);

  const {
    data: templatesData,
    loading: templatesLoading,
    refresh: refreshTemplates,
  } = useApi(getPromptTemplates, { immediate: true });

  useEffect(() => {
    refreshTemplates();
  }, [refreshTemplates]);

  const filteredData = useMemo(() => {
    const data = templatesData?.data || [];
    if (!searchText) return data;

    const lowerSearchText = searchText.toLowerCase();
    return data.filter(
      (item) =>
        item.code.toLowerCase().includes(lowerSearchText) ||
        item.name.toLowerCase().includes(lowerSearchText)
    );
  }, [templatesData?.data, searchText]);

  const handleEdit = (template: PromptTemplate) => {
    setEditingTemplate(template);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingTemplate(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    refreshTemplates();
  };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 200,
      sorter: (a: PromptTemplate, b: PromptTemplate) => a.code.localeCompare(b.code),
      render: (code: string) => (
        <Tag color="blue" icon={<FileTextOutlined />}>
          {code}
        </Tag>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 300,
      sorter: (a: PromptTemplate, b: PromptTemplate) => a.name.localeCompare(b.name),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => (
        <Badge
          status={isActive ? 'success' : 'default'}
          text={isActive ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      title: 'Variables',
      dataIndex: 'variables',
      key: 'variables',
      width: 120,
      render: (variables: string[] | null) => (
        <span>{variables ? variables.length : 0} vars</span>
      ),
    },
    {
      title: 'Modified At',
      dataIndex: 'modifiedAt',
      key: 'modifiedAt',
      width: 180,
      sorter: (a: PromptTemplate, b: PromptTemplate) => 
        new Date(a.modifiedAt).getTime() - new Date(b.modifiedAt).getTime(),
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: PromptTemplate) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>
            Prompt Templates
          </Title>
          <Search
            placeholder="Search by code or name"
            allowClear
            onSearch={(value) => setSearchText(value)}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            enterButton={<SearchOutlined />}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={templatesLoading}
          scroll={{ x: 1200 }}
          pagination={{
            current: currentPage,
            pageSize: CUSTOM_PAGE_SIZE,
            total: filteredData.length,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} templates`,
          }}
        />
      </Card>

      <EditPromptTemplateModal
        visible={isModalVisible}
        template={editingTemplate}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default PromptTemplates;
